import type { DocOptionEffect, OptionEffectOverrides } from "building-types/options/optionEffect";
import type { DocOptionSet } from "building-types/options/optionSet";
import type { OptionSetType } from "building-types/options/optionSetTypes";
import type { RWTable, Table } from "common/data/table";
import objectIsEmpty from "common/utils/objectIsEmpty";

/**
 * Constants
 */

/**
 * IDs cannot start with a number due to their use in formula evaluation,
 * so we add a prefix to them.
 */

export const OPTION_SET_ID_PREFIX = "optionSet_";

export const OPTION_ID_PREFIX = "option_";

export const NONE_OPTION_ID_SUFFIX = "_NONE";

export function isOptionSetId(id: string): boolean {
  return id.startsWith(OPTION_SET_ID_PREFIX);
}

export function isOptionId(id: string): boolean {
  return id.startsWith(OPTION_ID_PREFIX);
}

export function filterOptionSetsByTypes(
  optionSets: Table<DocOptionSet>,
  optionSetTypes: OptionSetType[],
): Table<DocOptionSet> {
  const filteredOptionSets: RWTable<DocOptionSet> = {};

  for (const optionSetId in optionSets) {
    if (optionSetTypes.includes(optionSets[optionSetId].optionSetType)) {
      filteredOptionSets[optionSetId] = optionSets[optionSetId];
    }
  }

  return filteredOptionSets;
}

export function groupOptionEffectsByRuleId(optionEffects: Table<DocOptionEffect>): Record<string, DocOptionEffect[]> {
  const optionEffectsByRuleId: Record<string, DocOptionEffect[]> = {};
  for (const effect of Object.values(optionEffects ?? {})) {
    (optionEffectsByRuleId[effect.parentId] ??= []).push(effect);
  }
  return optionEffectsByRuleId;
}

export function getRuleEffects(optionEffects: Table<DocOptionEffect>, ruleId: string): Table<DocOptionEffect> {
  const effects = {};
  for (const effectId of Object.keys(optionEffects ?? {})) {
    const effect = optionEffects[effectId];
    if (effect.parentId === ruleId) {
      effects[effectId] = effect;
    }
  }

  return effects;
}
/**
 * Merges option effect overrides into the base option effect table
 * @param baseEffects The base option effects
 * @param optionEffectOverrides An array of option effect overrides ordered from lowest priority to highest
 * @returns merged option effects
 */

export function mergeOptionEffects(
  baseEffects: Table<DocOptionEffect>,
  optionEffectOverrides: OptionEffectOverrides[],
): Table<DocOptionEffect> {
  const mergedEffects = { ...baseEffects };
  for (const overrides of optionEffectOverrides ?? []) {
    for (const effectId of Object.keys(overrides ?? {})) {
      const effect = overrides[effectId];
      if (objectIsEmpty(effect)) {
        continue;
      }
      if (mergedEffects[effectId] && !objectIsEmpty(mergedEffects[effectId])) {
        mergedEffects[effectId] = { ...mergedEffects[effectId], ...effect } as DocOptionEffect;
      }
    }
  }

  return mergedEffects;
}
